import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlerts } from '@/contexts/AlertContext';
import { useTranslation } from '@/i18n/I18nContext';
import { toast } from 'sonner';
import {
  CalendarPlus, CalendarClock, CalendarX, MapPin, MessageCircle, X,
} from 'lucide-react';

const ALERT_CONFIG = {
  booking_created: { icon: CalendarPlus, color: 'text-emerald-600', toastType: 'success' },
  booking_updated: { icon: CalendarClock, color: 'text-blue-600', toastType: 'info' },
  booking_cancelled: { icon: CalendarX, color: 'text-red-600', toastType: 'warning' },
  customer_arrival: { icon: MapPin, color: 'text-amber-600', toastType: 'info' },
  manual_mode_message: { icon: MessageCircle, color: 'text-purple-600', toastType: 'message' },
  eva_off_message: { icon: MessageCircle, color: 'text-orange-600', toastType: 'warning' },
};

function formatAlertTime(isoStr) {
  if (!isoStr) return '';
  try {
    return new Date(isoStr).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function formatBookingTime(startAt) {
  if (!startAt) return '';
  try {
    const d = new Date(startAt);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
}

function getAlertTitle(type, t) {
  const titles = {
    booking_created: t('alerts.bookingCreated'),
    booking_updated: t('alerts.bookingUpdated'),
    booking_cancelled: t('alerts.bookingCancelled'),
    customer_arrival: t('alerts.customerArrival'),
    manual_mode_message: t('alerts.manualModeMessage'),
    eva_off_message: t('alerts.evaOffMessage') !== 'alerts.evaOffMessage' ? t('alerts.evaOffMessage') : 'Messaggio (EVA OFF)',
  };
  return titles[type] || type;
}

function buildAlertBody(type, data, t) {
  const d = data || {};
  switch (type) {
    case 'booking_created': {
      const parts = [];
      if (d.girl_name) parts.push(`${t('alerts.girl')}: ${d.girl_name}`);
      if (d.start_at) parts.push(`${t('alerts.time')}: ${formatBookingTime(d.start_at)}`);
      if (d.duration_minutes) parts.push(`${d.duration_minutes} ${t('alerts.min')}`);
      if (d.client_phone) parts.push(d.client_phone);
      if (d.source) parts.push(d.source === 'whatsapp' ? t('alerts.whatsapp') : t('alerts.manual'));
      return parts.join(' · ');
    }
    case 'booking_updated': {
      const parts = [];
      if (d.girl_name) parts.push(`${t('alerts.girl')}: ${d.girl_name}`);
      if (d.start_at) parts.push(`${t('alerts.time')}: ${formatBookingTime(d.start_at)}`);
      if (d.changed_fields?.length) parts.push(`${t('alerts.changed')}: ${d.changed_fields.join(', ')}`);
      return parts.join(' · ');
    }
    case 'booking_cancelled': {
      const parts = [];
      if (d.girl_name) parts.push(d.girl_name);
      if (d.start_at) parts.push(formatBookingTime(d.start_at));
      if (d.client_phone) parts.push(d.client_phone);
      return parts.join(' · ');
    }
    case 'customer_arrival': {
      const parts = [];
      if (d.phone) parts.push(d.phone);
      if (d.name) parts.push(d.name);
      if (d.arrival_status) {
        const statusKey = `alerts.arrival${d.arrival_status.charAt(0).toUpperCase() + d.arrival_status.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`;
        const translated = t(statusKey);
        if (translated !== statusKey) parts.push(translated);
      }
      if (d.snippet) parts.push(`"${d.snippet.slice(0, 60)}"`);
      return parts.join(' · ');
    }
    case 'manual_mode_message': {
      const parts = [];
      if (d.phone) parts.push(d.phone);
      if (d.name) parts.push(d.name);
      if (d.snippet) parts.push(`"${d.snippet.slice(0, 80)}"`);
      return parts.join(' · ');
    }
    case 'eva_off_message': {
      const parts = [];
      if (d.phone) parts.push(d.phone);
      if (d.name) parts.push(d.name);
      parts.push('EVA OFF');
      if (d.snippet) parts.push(`"${d.snippet.slice(0, 80)}"`);
      return parts.join(' · ');
    }
    default:
      return JSON.stringify(d).slice(0, 100);
  }
}

function getNavTarget(type) {
  if (type === 'manual_mode_message') return '/inbox';
  if (type === 'eva_off_message') return '/inbox';
  if (type === 'customer_arrival') return '/inbox';
  return '/calendar';
}

export function AlertToastManager() {
  const { newAlerts, dismissNew } = useAlerts();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shownRef = React.useRef(new Set());

  useEffect(() => {
    for (const alert of newAlerts) {
      if (shownRef.current.has(alert.id)) continue;
      shownRef.current.add(alert.id);

      const title = getAlertTitle(alert.type, t);
      const body = buildAlertBody(alert.type, alert.data, t);
      const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.booking_created;
      const Icon = config.icon;
      const navTarget = getNavTarget(alert.type);

      toast(title, {
        description: body,
        duration: 8000,
        icon: <Icon className={`w-5 h-5 ${config.color}`} />,
        action: {
          label: alert.type.includes('message') || alert.type === 'customer_arrival'
            ? t('alerts.viewInbox')
            : t('alerts.viewCalendar'),
          onClick: () => navigate(navTarget),
        },
        onAutoClose: () => dismissNew(alert.id),
        onDismiss: () => dismissNew(alert.id),
      });
    }
  }, [newAlerts, t, navigate, dismissNew]);

  return null;
}

export function AlertFeedPanel({ maxItems = 15 }) {
  const { alerts, clearAlerts, soundEnabled, toggleSound, deleteAlert } = useAlerts();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const displayed = alerts.slice(0, maxItems);

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="alert-feed-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800">{t('alerts.recentAlerts')}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSound}
            className={`text-xs px-2 py-1 rounded ${soundEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
            data-testid="toggle-alert-sound"
            title={soundEnabled ? t('alerts.soundOn') : t('alerts.soundOff')}
          >
            {soundEnabled ? '🔔' : '🔕'}
          </button>
          {alerts.length > 0 && (
            <button
              onClick={clearAlerts}
              className="text-xs text-slate-400 hover:text-slate-600"
              data-testid="clear-alerts"
            >
              {t('alerts.clearAll')}
            </button>
          )}
        </div>
      </div>
      <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-50">
        {displayed.length === 0 ? (
          <p className="text-center text-sm text-slate-400 py-6">{t('alerts.noAlerts')}</p>
        ) : (
          displayed.map(alert => {
            const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.booking_created;
            const Icon = config.icon;
            const title = getAlertTitle(alert.type, t);
            const body = buildAlertBody(alert.type, alert.data, t);
            const navTarget = getNavTarget(alert.type);

            return (
              <div
                key={alert.id}
                className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3 group"
                data-testid={`alert-item-${alert.id}`}
              >
                <button
                  onClick={() => navigate(navTarget)}
                  className="flex items-start gap-3 flex-1 min-w-0 text-left"
                  data-testid={`alert-nav-${alert.id}`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-800">{title}</span>
                      <span className="text-[10px] text-slate-400 ml-2 flex-shrink-0">
                        {formatAlertTime(alert.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5">{body}</p>
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteAlert(alert.id); }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-slate-200 flex-shrink-0"
                  data-testid={`alert-delete-${alert.id}`}
                  title={t('alerts.dismiss')}
                >
                  <X className="w-3 h-3 text-slate-400" />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
